import { ExecutionResult } from "graphql/execution/execute";
import { useState } from "react";
import { createContainer } from "unstated-next";
import { reverse, without, values, flatMap } from "lodash";

export interface Alert {
  type: "success" | "danger" | "warn" | "info";
  message: React.ReactNode;
}

const defaultTimeout = 10000;

function useAppAlertsContainer(initialState = []) {
  const [alerts, setAlerts] = useState<Alert[]>(initialState);

  const addAlerts = (...newAlerts: Alert[]) => {
    setAlerts((oldAlerts) => [...reverse(newAlerts), ...oldAlerts]);
  };

  const addAlertWithType = (
    message: React.ReactNode,
    type: Alert["type"],
    options?: { timeout?: number }
  ) => {
    const al: Alert = { type, message };

    addAlerts(al);

    setTimeout(
      () => removeAlert(al),
      options?.timeout == null ? defaultTimeout : options?.timeout
    );
  };

  const addSuccessAlert = (
    message: React.ReactNode,
    options?: { timeout?: number }
  ) => {
    addAlertWithType(message, "success", options);
  };

  const addDangerAlert = (
    message: React.ReactNode,
    options?: { timeout?: number }
  ) => {
    addAlertWithType(message, "danger", options);
  };

  const addInfoAlert = (
    message: React.ReactNode,
    options?: { timeout?: number }
  ) => {
    addAlertWithType(message, "info", options);
  };

  const addErrorsFromMutation = (mutationResult: ExecutionResult<any>) => {
    const errors = flatMap(values(mutationResult?.data), (item) => {
      return item?.errors;
    });

    const errorMessages = flatMap(errors, (err) => {
      return err?.messages;
    });

    const errorAlerts = flatMap(errorMessages, (msg) => {
      return { type: "danger", message: msg } as Alert;
    });

    addAlerts(...errorAlerts);

    return errorAlerts;
  };

  const removeAlert = (al: Alert) => {
    setAlerts((oldAlerts) => without(oldAlerts, al));
  };

  const clearAlerts = () => {
    setAlerts([]);
  };

  return {
    addAlerts,
    addSuccessAlert,
    addDangerAlert,
    addInfoAlert,
    removeAlert,
    clearAlerts,
    alerts,
    addErrorsFromMutation,
  };
}

export const AppAlerts = createContainer(useAppAlertsContainer);

export function useAppAlerts() {
  return AppAlerts.useContainer();
}
